import { Action } from './base'

import type { Moment } from 'moment'
import API from 'app/services/company/companyApi'
import {
  AggregatedCompanyDetailsModel,
  CompanyDetailsModel,
} from 'app/models/companyDetailsModel'
import { CreateCompanyModel } from 'app/models/createCompanyModel'
import { CompanyType } from 'app/constants/companyType'
import { CompanyActiveDriverModel } from 'app/models/company/CompanyActiveDriverModel'
import { CurrentCompanyModel } from 'app/models/company/currentCompanyModel'
import { CompanyConfigurationModel } from 'app/models/company/companyConfigurationModel'
import { showMessage } from '@/modules/shared/stores/messageStore'
import { MessageType } from 'app/constants/messageType'
import { getPredefinedPackage } from 'app/services/company/predefinedPackageResolver'
import { CompanyFilterResult } from 'app/models/company/companyFilter'
import { GusCompanyData } from 'app/models/company/gusCompanyData'
import {
  PaymentCompanyModel,
  PaymentCompanyResultModel,
} from 'app/models/company/paymentCompanyModel'
import { PackageHistoryModel } from 'app/models/company/packageHistoryModel'

export enum ActionTypeKeys {
  GET_COMPANY_LIST = 'GET_COMPANY_LIST',
  GET_DEMO_COMPANY_LIST = 'GET_DEMO_COMPANY_LIST',
  GET_COMPANY_SIMPLE_LIST = 'GET_COMPANY_SIMPLE_LIST',
  GET_COMPANY_SIMPLE_LIST_FOR_GROUPS = 'GET_COMPANY_SIMPLE_LIST_FOR_GROUPS',
  GET_AGGREGATED_COMPANY_LIST = 'GET_AGGREGATED_COMPANY_LIST',
  GET_WPF_COMPANY_LIST = 'GET_WPF_COMPANY_LIST',
  GET_COMPANY_ACTIVE_DRIVERS = 'GET_COMPANY_ACTIVE_DRIVERS',
  GET_COMPANY = 'GET_COMPANY',
  ADD_NEW_COMPANY = 'ADD_NEW_COMPANY',
  EDIT_COMPANY = 'EDIT_COMPANY',
  EDIT_DEMO_COMPANY = 'EDIT_DEMO_COMPANY',
  DELETE_COMPANIES = 'DELETE_COMPANIES',
  GET_COMPANY_DETAILS = 'GET_COMPANY_DETAILS',
  GET_CURRENT_COMPANY = 'GET_CURRENT_COMPANY',
  RESET_COMPANY = 'RESET_COMPANY',
  IMPORT_FROM_GUS = 'IMPORT_FROM_GUS',
  GET_PAYMENT_COMPANIES = 'GET_PAYMENT_COMPANIES',
  EDIT_PAYMENT_COMPANY = 'EDIT_PAYMENT_COMPANY',
  UPLOAD_FILE = 'UPLOAD_FILE',
  PACKAGE_HISTORY = 'PACKAGE_HISTORY',
  RESET_PAYMENT_STATE = 'RESET_PAYMENT_STATE',
  ACTIVATE_COMPANY = 'ACTIVATE_COMPANY',
  DEACTIVATE_COMPANY = 'DEACTIVATE_COMPANY',
}

export type FileUploadAction = Action<typeof ActionTypeKeys.UPLOAD_FILE, any>

export type GetCompaniesAction = Action<
  typeof ActionTypeKeys.GET_COMPANY_LIST,
  CompanyDetailsModel[]
>

export type GetDemoCompaniesAction = Action<
  typeof ActionTypeKeys.GET_DEMO_COMPANY_LIST,
  CompanyDetailsModel[]
>

export type GetPaymentCompaniesAction = Action<
  typeof ActionTypeKeys.GET_PAYMENT_COMPANIES,
  PaymentCompanyModel[]
>

export type GetCompaniesSimpleAction = Action<
  typeof ActionTypeKeys.GET_COMPANY_SIMPLE_LIST,
  CompanyDetailsModel[]
>

export type GetCompaniesSimpleForGroupsAction = Action<
  typeof ActionTypeKeys.GET_COMPANY_SIMPLE_LIST_FOR_GROUPS,
  CompanyDetailsModel[]
>

export type GetPackageHistoryAction = Action<
  typeof ActionTypeKeys.PACKAGE_HISTORY,
  PackageHistoryModel[]
>

export type GetAggregatedCompaniesAction = Action<
  typeof ActionTypeKeys.GET_AGGREGATED_COMPANY_LIST,
  AggregatedCompanyDetailsModel
>

export type GetWpfCompaniesAction = Action<
  typeof ActionTypeKeys.GET_WPF_COMPANY_LIST,
  any
>

export type GetCompanyActiveDriversAction = Action<
  typeof ActionTypeKeys.GET_COMPANY_ACTIVE_DRIVERS,
  CompanyActiveDriverModel[]
>

export type CreateCompanyAction = Action<
  typeof ActionTypeKeys.ADD_NEW_COMPANY,
  CompanyDetailsModel
>

export type EditPaymentCompanyAction = Action<
  typeof ActionTypeKeys.EDIT_PAYMENT_COMPANY,
  PaymentCompanyModel
>

export type EditDemoCompanyAction = Action<
  typeof ActionTypeKeys.EDIT_DEMO_COMPANY,
  CompanyDetailsModel
>

export type ResetPaymentState = Action<
  typeof ActionTypeKeys.RESET_PAYMENT_STATE,
  any
>

export type ResetCompanyAction = Action<
  typeof ActionTypeKeys.RESET_COMPANY,
  any
>

export type GetCompanyAction = Action<
  typeof ActionTypeKeys.GET_COMPANY,
  CompanyDetailsModel
>
export type GetCurrentCompanyAction = Action<
  typeof ActionTypeKeys.GET_CURRENT_COMPANY,
  CurrentCompanyModel
>
export type GetCompanyDetailsAction = Action<
  typeof ActionTypeKeys.GET_COMPANY_DETAILS,
  CompanyDetailsModel
>
export type ImportFromGusAction = Action<
  typeof ActionTypeKeys.IMPORT_FROM_GUS,
  GusCompanyData
>
export type EditCompanyAction = Action<
  typeof ActionTypeKeys.EDIT_COMPANY,
  CompanyDetailsModel
>
export type DeleteCompaniesAction = Action<
  typeof ActionTypeKeys.DELETE_COMPANIES,
  Array<string>
>

export const resetCompany = (): any => {
  return {
    type: ActionTypeKeys.RESET_COMPANY,
  }
}

export const addCompany = async (
  companyInput: CreateCompanyModel,
): Promise<any> => {
  const api = API.create()
  try {
    const response = await api.addCompany(companyInput)
    const data = response.content.data
    const company: CompanyDetailsModel =
      data != '' ? mapCompanyFields(data) : null

    return company
  } catch (error) {
    if (error.status.status == 403) {
      showMessage({
        text: `administrationPanel-companies.company-not-added`,
        details: `errorCode.UNAUTHORIZED`,
        type: MessageType.ERROR,
        translate: true,
      })
      return { error: error.status.status }
    }
    return { error: error.status.data.code }
  }
}

export const resetPaymentState = async (companyId: string): Promise<any> => {
  const api = API.create()
  const response = await api.resetPaymentState(companyId)
  const data = response.content.data

  return data
}

export const editPaymentCompany = async (
  companyInput: PaymentCompanyModel,
  force: boolean,
): Promise<any> => {
  const api = API.create()

  try {
    const response = await api.editPaymentCompany(companyInput, force)
    const data = response.content.data
    const company: PaymentCompanyModel = data

    return company
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const editDemoCompany = async (
  companyId: string,
  dateTo: Moment,
  name: string,
): Promise<any> => {
  const api = API.create()

  try {
    const response = await api.editDemoCompany(companyId, dateTo, name)
    const data = response.content.data
    const company: boolean = data

    return company
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const editCompany = async (
  company: CompanyDetailsModel,
): Promise<any> => {
  const api = API.create()

  try {
    const response = await api.editCompany({
      id: company.id,
      companyName: company.companyName,
      krs: company.krs,
      useLocalImport: company.useLocalImport,
      nip: company.nip,
      regon: company.regon,
      email: company.email,
      notificationsEmail: company.notificationsEmail,
      contactEmail: company.contactEmail,
      contactCCEmail: company.contactCCEmail,
      contactPhone: company.contactPhone,
      licenseNumber: company.licenseNumber,
      street: company.street,
      houseNumber: company.houseNumber,
      buildingNumber: company.buildingNumber,
      postalCode: company.postalCode,
      city: company.city,
      userRoles: company.userRoles,
      activeDriversLimits: company.activeDriversLimits,
      package: company.package,
      packageMultiplier: company.packageMultiplier,
      subscriptionExpirationDate: company.subscriptionExpirationDate,
      suspendedFrom: company.suspendedFrom,
      suspensionDate: company.suspensionDate,
      suspendedTo: company.suspendedTo,
      monthlySubscription: company.monthlySubscription,
      paid: company.paid,
      suspended: company.suspended,
      isEwiSystem: company.isEwiSystem,
      discount: company.discount,
      discountValue: company.discountValue,
      additionalPaidDrivers: company.additionalPaidDrivers,
      enablePaymentReminder: company.enablePaymentReminder,
      paymentReminderDuration: company.paymentReminderDuration,
      paymentReminderDate: company.paymentReminderDate,
      manuallyEnteredUnpaidDrivers: company.manuallyEnteredUnpaidDrivers,
      unpaidDrivers: company.unpaidDrivers,
      note: company.note,
      paymentDays: company.paymentDays,
      boundedPackage: company.boundedPackage,
      paymentDaysOverride: company.paymentDaysOverride,
      newActiveDriversLimits: company.newActiveDriversLimits,
      newBoundedPackage: company.newBoundedPackage,
      newPackage: company.newPackage,
      newPackageFrom: company.newPackageFrom,
      newPackageTo: company.newPackageTo,
      newPackageMultiplier: company.newPackageMultiplier,
      newMonthlySubscription: company.newMonthlySubscription,
      newSubscriptionExpirationDate: company.newSubscriptionExpirationDate,
      bonusAmount: company.bonusAmount,
      bonusMonths: company.bonusMonths,
      bonusStartedAt: company.bonusStartedAt,
      paidDriversInPreviousMonth: company.paidDriversInPreviousMonth,
      paymentNetAmount: company.paymentNetAmount,
      activationDate: company.activationDate,
      sellDate: company.sellDate,
      paymentDriversCount: company.paymentDriversCount,
      paymentMonths: company.paymentMonths,
      currentDriversModificationDate: company.currentDriversModificationDate,
      currentDriversOverflow: company.currentDriversOverflow,
      forcePaymentSave: company.forcePaymentSave,
      paymentDate: company.paymentDate,
      paymentCreationDate: company.paymentCreationDate,
      longPaid: company.longPaid,
      longPaymentDate: company.longPaymentDate,
      longPaymentCreationDate: company.longPaymentCreationDate,
      longPaymentMonths: company.longPaymentMonths,
      longPaymentNetAmount: company.longPaymentNetAmount,
      timesheetModuleEnabled: company.timesheetModuleEnabled,
      blockTimesheetCalculation: company.blockTimesheetCalculation,
      isServiceCompany: company.type === CompanyType.SERVICE,
      groups: company.groupList,
    })
    return response.content.data
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const deleteCompanies = async (ids: Array<string>): Promise<any> => {
  try {
    const api = API.create()
    await api.deleteCompany(ids)

    return ids
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const activateCompany = async (
  companyIds: Array<string>,
  isActive: boolean,
): Promise<any> => {
  const api = API.create()
  try {
    await api.activateCompany(companyIds, isActive)

    return companyIds
  } catch (error) {
    return error.status.data
  }
}

export const getActiveDrivers = async (companyId?: string): Promise<any> => {
  const api = API.create()
  const response = await api.getActiveDrivers(companyId)

  return response.content.data as Array<CompanyActiveDriverModel>
}

export const getCompanies = async (
  tenantId?: string,
  includeDeleted: boolean = false,
): Promise<any> => {
  const api = API.create()
  const response = await api.getCompanies(tenantId, includeDeleted)
  const data =
    tenantId == null
      ? response.content.data
      : response.content.data.childCompanies
  const companies: Array<CompanyDetailsModel> = data.map((c) =>
    mapCompanyFields(c),
  )
  return companies
}

export const getDemoCompanies = async (
  tenantId?: string,
  take?: number,
  skip?: number,
  orderBy?: string,
  descending?: boolean,
  filters?: CompanyFilterResult,
  includeDeleted: boolean = false,
): Promise<any> => {
  const api = API.create()
  const response = await api.getDemoCompanies(
    tenantId,
    take,
    skip,
    orderBy,
    descending,
    filters,
    includeDeleted,
  )
  const data = response.content.data
  const returnedCompanies = data.companies

  const companies: Array<CompanyDetailsModel> = returnedCompanies.map((c) =>
    mapCompanyFields(c),
  )
  const aggregatedCompanies: AggregatedCompanyDetailsModel = {
    companies: companies,
    activeDriversCount: data.activeDriversCount,
    proActiveDriversCount: data.proActiveDriversCount,
    companiesCount: data.companiesCount,
  }

  return aggregatedCompanies
}

export const getPaymentCompanies = async (
  source: number,
  exported: boolean,
  notExported: boolean,
): Promise<any> => {
  const api = API.create()
  const response = await api.getPaymentCompanies(source, exported, notExported)
  const data = response.content.data
  const companies: PaymentCompanyResultModel = data

  return companies
}

export const getPackageHistory = async (companyId: string): Promise<any> => {
  const api = API.create()
  const response = await api.getPackageHistory(companyId)
  const data = response.content.data
  const history: Array<PackageHistoryModel> = data

  return history
}

export const uploadFile = async (file: any): Promise<any> => {
  const api = API.create()
  const response = await api.uploadFile(file)
  const data = response.content.data

  return data
}

export const getCompaniesSimple = async (
  tenantId?: string,
  includeDeleted: boolean = false,
  includeChildren: boolean = false,
): Promise<any> => {
  const api = API.create()
  const response = await api.getCompaniesSimple(
    tenantId,
    includeDeleted,
    includeChildren,
  )
  const data = response.content.data

  const companies: Array<CompanyDetailsModel> = data.map((c) =>
    mapCompanyFields(c),
  )
  return companies
}

export const getCompaniesSimpleForGroups = async (
  ids: Array<string>,
  includeDeleted: boolean = false,
): Promise<any> => {
  const api = API.create()
  const response = await api.getCompaniesSimpleForGroups(ids, includeDeleted)
  const data = response.content.data

  const companies: Array<CompanyDetailsModel> = data.map((c) =>
    mapCompanyFields(c),
  )
  return companies
}

export const getFilteredCompanies = async (
  tenantId?: string,
  take?: number,
  skip?: number,
  orderBy?: string,
  descending?: boolean,
  filters?: CompanyFilterResult,
  includeDeleted: boolean = false,
): Promise<any> => {
  const api = API.create()
  const response = await api.getFilteredCompanies(
    tenantId,
    take,
    skip,
    orderBy,
    descending,
    filters,
    includeDeleted,
  )
  const data = response.content.data
  const returnedCompanies = data.companies

  const companies: Array<CompanyDetailsModel> = returnedCompanies.map((c) =>
    mapCompanyFields(c),
  )
  const aggregatedCompanies: AggregatedCompanyDetailsModel = {
    companies: companies,
    activeDriversCount: data.activeDriversCount,
    proActiveDriversCount: data.proActiveDriversCount,
    companiesCount: data.companiesCount,
  }

  return aggregatedCompanies
}

export const getWpfCompanies = async (
  companyId: string,
  serverId: string,
): Promise<any> => {
  try {
    const api = API.create()
    const response = await api.getWpfCompanies(companyId, serverId)
    const data: Array<CompanyConfigurationModel> = response.content.data
    return data
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const getCompany = async (
  companyId: string,
  ignoreMigration: boolean,
): Promise<any> => {
  let company: CompanyDetailsModel = null
  if (companyId != null) {
    company = await getCompanyInner(companyId, ignoreMigration)
  }

  return company
}

export const importFromGus = async (nip: string): Promise<any> => {
  const api = API.create()
  const response = await api.importFromGus(nip)
  const company = response.content.data as CurrentCompanyModel

  return company
}

export const getCompanyDetails = async (
  companyId: string,
  ignoreMigration: boolean,
): Promise<any> => {
  let company: CompanyDetailsModel = null
  if (companyId != null) {
    company = await getCompanyInner(companyId, ignoreMigration)
  }

  return company
}

export const getCurrentCompany = async (companyId: string): Promise<any> => {
  const api = API.create()
  const response = await api.getCurrentCompany(companyId)
  const currentCompany = response.content.data as CurrentCompanyModel

  return currentCompany
}

async function getCompanyInner(companyId: string, ignoreMigration: boolean) {
  const api = API.create()
  const response = await api.getCompany(companyId, ignoreMigration)
  const data = response.content.data

  const company: CompanyDetailsModel = mapCompanyFields(data)
  return company
}

export const mapCompanyFields = (data: any): CompanyDetailsModel => {
  const serverNamesFormatted =
    data.serverNames != null && data.serverNames.length > 0
      ? data.serverNames
          .map((r) => r)
          .filter((elem, pos, arr) => arr.indexOf(elem) == pos)
          .join(', ')
      : data.serverName != null && data.serverName != ''
        ? data.serverName
        : '-'

  const result: CompanyDetailsModel = {
    id: data.id,
    companyName: data.companyName,
    type: data.type ? CompanyType.SERVICE : CompanyType.NORMAL,
    uniqueIdentifier: data.uniqueIdentifier,
    databaseId: data.databaseId,
    databaseName: data.databaseName,
    serverId: data.serverId,
    isDeleted: data.isDeleted,
    isActive: data.isActive,
    servers: data.servers,
    serverNames: data.serverNames,
    serverNamesFormatted: serverNamesFormatted,
    serverName: data.serverName,
    tenant: data.tenant != null ? mapCompanyFields(data.tenant) : null,
    krs: data.krs,
    nip: data.nip,
    regon: data.regon,
    email: data.email,
    accountingOnly: data.accountingOnly,
    temporary: data.temporary,
    subscriptionExpirationDate: data.subscriptionExpirationDate,
    monthlySubscriptionDateOverride: data.monthlySubscriptionDateOverride,
    paymentDaysOverride: data.paymentDaysOverride,
    suspendedFrom: data.suspendedFrom,
    suspendedTo: data.suspendedTo,
    suspensionDate: data.suspensionDate,
    boundedPackage: data.boundedPackage,
    monthlySubscription: data.monthlySubscription,
    paid: data.paid,
    suspended: data.suspended,
    notificationsEmail: data.notificationsEmail,
    contactEmail: data.contactEmail,
    contactCCEmail: data.contactCCEmail,
    contactPhone: data.contactPhone,
    licenseNumber: data.licenseNumber,
    street: data.street,
    buildingNumber: data.buildingNumber,
    houseNumber: data.houseNumber,
    postalCode: data.postalCode,
    city: data.city,
    activeDriversLimits: data.activeDriversLimits,
    package: data.package != null ? getPredefinedPackage(data.package) : null,
    packageMultiplier: data.packageMultiplier,
    packageAmount: data.packageAmount,
    createdBy: data.createdBy,
    tenantId: data.tenantId,
    createdDate: data.createdDate,
    deletedDate: data.deletedDate,
    modifiedBy: data.modifiedBy,
    modifiedDate: data.modifiedDate,
    childCompanies: data.childCompanies
      ? data.childCompanies
          .filter((c) => c != null)
          .map((c) => mapCompanyFields(c))
      : null,
    isValid: data.isValid,
    groupList: data.groupList || [],
    lastErrorMessage: data.lastErrorMessage,
    useLocalImport: data.useLocalImport,
    driversCount: data.driversCount,
    currentDriversCount: data.currentDriversCount,
    size: data.size,
    proDriversCount: data.proDriversCount,
    proDriversFromPreviousMonthCount: data.proDriversFromPreviousMonthCount,
    lastSynchronizationDate: data.lastSynchronizationDate,
    synchronizationSource: data.synchronizationSource,
    isPro: data.isPro,
    isEwiSystem: data.isEwiSystem,
    discount: data.discount,
    discountValue: data.discountValue,
    hasKeyProFeature: data.hasKeyProFeature,
    proFeatureDisabled: data.proFeatureDisabled,
    additionalPaidDrivers: data.additionalPaidDrivers,
    enablePaymentReminder: data.enablePaymentReminder,
    paymentReminderDuration: data.paymentReminderDuration,
    paymentReminderDate: data.paymentReminderDate,
    manuallyEnteredUnpaidDrivers: data.manuallyEnteredUnpaidDrivers,
    unpaidDrivers: data.unpaidDrivers,
    previousUnpaidDrivers: data.previousUnpaidDrivers,
    databaseVersion: data.databaseVersion,
    note: data.note,
    paymentDays: data.paymentDays,
    newActiveDriversLimits: data.newActiveDriversLimits,
    newPackage: data.newPackage,
    newPackageMultiplier: data.newPackageMultiplier,
    newPackageFrom: data.newPackageFrom,
    newPackageTo: data.newPackageTo,
    newBoundedPackage: data.newBoundedPackage,
    newMonthlySubscription: data.newMonthlySubscription,
    newSubscriptionExpirationDate: data.newSubscriptionExpirationDate,
    multipleKeys: data.multipleKeys,
    multipleLogsFromKeys: data.multipleLogsFromKeys,
    bonusAmount: data.bonusAmount,
    bonusMonths: data.bonusMonths,
    bonusStartedAt: data.bonusStartedAt,
    paidDriversInPreviousMonth: data.paidDriversInPreviousMonth,
    paymentNetAmount: data.paymentNetAmount,
    activationDate: data.activationDate,
    sellDate: data.sellDate,
    paymentDriversCount: data.paymentDriversCount,
    commisionAmount: data.commisionAmount,
    commisionMonth: data.commisionMonth,
    commisionValue: data.commisionValue,
    commisionEnabled: data.commisionEnabled,
    invoiceNumber: data.invoiceNumber,
    manualPayment: data.manualPayment,
    paymentDate: data.paymentDate,
    paymentCreationDate: data.paymentCreationDate,
    providerInvoiceId: data.providerInvoiceId,
    paymentMonths: data.paymentMonths,
    currentDriversModificationDate: data.currentDriversModificationDate,
    currentDriversOverflow: data.currentDriversOverflow,
    longPaid: data.longPaid,
    longPaymentDate: data.longPaymentDate,
    longPaymentMonths: data.longPaymentMonths,
    longPaymentCreationDate: data.longPaymentCreationDate,
    longPaymentNetAmount: data.longPaymentNetAmount,
    timesheetModuleEnabled: data.timesheetModuleEnabled,
    blockTimesheetCalculation: data.blockTimesheetCalculation,
    demoTimeRemaining: data.demoTimeRemaining,
    demoUntil: data.demoUntil,
    ipAddress: data.ipAddress,
    running: data.running,
    demoKey: data.demoKey,
    group: data.group,
    permanentlyBlocked: data.permanentlyBlocked,
    previousMonthDriversAdjustmentDate: data.previousMonthDriversAdjustmentDate,
    unpaidDriversModificationDate: data.unpaidDriversModificationDate,
  }

  // Wyliczanie `status` / `typeFormatted` wyleciało razem ze starą siatką firm:
  // nikt tych pól nie czytał, a środkowa gałąź `status` była martwa (ten sam warunek
  // co poprzednia). Ikonę statusu liczy dziś `statusCell` w `AdminCompaniesPage`,
  // wprost z `isValid` i `childCompanies`.

  return result
}

export type CompanyListActions =
  | GetPaymentCompaniesAction
  | GetCompaniesAction
  | GetDemoCompaniesAction
  | GetCompaniesSimpleAction
  | GetCompaniesSimpleForGroupsAction
  | GetAggregatedCompaniesAction
  | GetWpfCompaniesAction
  | GetCompanyActiveDriversAction
  | GetCompanyAction
  | GetCompanyDetailsAction
  | CreateCompanyAction
  | EditCompanyAction
  | DeleteCompaniesAction
  | GetCurrentCompanyAction
  | ResetCompanyAction
  | ImportFromGusAction
  | EditPaymentCompanyAction
  | EditDemoCompanyAction
  | FileUploadAction
  | GetPackageHistoryAction
  | ResetPaymentState
